import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const SessionContext = createContext(null);

export const useSession = () => useContext(SessionContext);

export const SessionProvider = ({ children }) => {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  // Fetch enrolled courses as "sessions"
  useEffect(() => {
    if (!user) return;
    const fetchSessions = async () => {
      try {
        const res = await fetch(`${API}/courses/my-courses`, { credentials: 'include' });
        if (res.ok) {
          const courses = await res.json();
          setSessions(courses);
          if (courses.length > 0 && !selectedSessionId) {
            setSelectedSessionId(courses[0].course_id);
          }
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchSessions();
  }, [user]);

  // Fetch session-specific data when session changes
  useEffect(() => {
    if (!selectedSessionId) return;
    const fetchSessionData = async () => {
      try {
        const [modulesRes, progressRes, emargementsRes, evalsRes, docsRes, sessionsRes] = await Promise.all([
          fetch(`${API}/courses/${selectedSessionId}/modules`, { credentials: 'include' }),
          fetch(`${API}/progress/course/${selectedSessionId}`, { credentials: 'include' }),
          fetch(`${API}/suivi-pedagogique`, { credentials: 'include' }),
          fetch(`${API}/certificates/check/${selectedSessionId}`, { method: 'POST', credentials: 'include' }),
          fetch(`${API}/documents/my-documents`, { credentials: 'include' }),
          fetch(`${API}/programme`, { credentials: 'include' })
        ]);

        const course = sessions.find(s => s.course_id === selectedSessionId);
        const modules = modulesRes.ok ? await modulesRes.json() : [];
        const progress = progressRes.ok ? await progressRes.json() : null;
        const suiviData = emargementsRes.ok ? await emargementsRes.json() : [];
        const certCheck = evalsRes.ok ? await evalsRes.json() : {};
        const allDocs = docsRes.ok ? await docsRes.json() : [];
        const allSessions = sessionsRes.ok ? await sessionsRes.json() : [];

        // Filter data for current session/course
        const sessionSuivi = suiviData.find(s => s.course_id === selectedSessionId) || {};
        const sessionDocs = allDocs.filter(d => d.course_id === selectedSessionId || d.visibility === 'all');
        const sessionSessions = allSessions.filter(s => s.course_id === selectedSessionId);

        setSessionData({
          course,
          modules,
          progress,
          suivi: sessionSuivi,
          certCheck,
          documents: sessionDocs,
          planning: sessionSessions,
          pendingEmargements: sessionSuivi.emargements_total - (sessionSuivi.emargements_signed || 0),
          pendingEvaluations: (sessionSuivi.evaluations_total || 0) - (sessionSuivi.evaluations_completed || 0),
        });
      } catch (e) { console.error(e); }
    };
    fetchSessionData();
  }, [selectedSessionId, sessions]);

  return (
    <SessionContext.Provider value={{
      sessions,
      selectedSessionId,
      setSelectedSessionId,
      sessionData,
      loading,
      currentCourse: sessions.find(s => s.course_id === selectedSessionId) || null
    }}>
      {children}
    </SessionContext.Provider>
  );
};
